import { useMemo, useState } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { coloresDe, porcentaje, entero, NEUTRO } from '../lib/graficas';

/**
 * LA DONA del HUB (0.46.0): una sola pieza para todas las gráficas de pastel. "Las gráficas de pastel siguen sin mostrar
 * porcentajes (debe aplicar para todas)" y "cuando se apaga, como se quita el porcentaje, se mueve todo".
 *
 *  - Cada rebanada con su % encima si cabe (≥ 6 % de lo que se ve); el tooltip dice "N · X %"; en el centro, el total.
 *  - La leyenda es una TABLA de ancho fijo: muestra · nombre (cortado con "…", completo al pasar el cursor) · cuántos
 *    (a la derecha, cifras alineadas) · % (a la derecha, ancho fijo). Todas las filas comparten las columnas, así que
 *    ningún largo de texto las mueve.
 *  - Apagar una fila NO la quita: se queda en su lugar, atenuada, con "—" en el %, y los demás % se recalculan con lo que
 *    se ve. Nada brinca. Es un botón con aria-pressed (teclado y lector de pantalla).
 *  - El color sigue a la categoría (por su lugar en la lista completa), no a lo que se ve: apagar una no repinta las demás.
 *  - `onAbrir`: tocar la rebanada o "Ver" abre el detalle de esa categoría.
 */
export interface Rebanada { clave: string; nombre: string; n: number; color?: string; titulo?: string }

const CSS = `
.dona { display: grid; gap: 8px; min-width: 0; }
.dona-graf { position: relative; }
.dona-centro { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; pointer-events: none; text-align: center; }
.dona-centro b { font-size: 22px; font-weight: 700; color: var(--ink); line-height: 1.1; }
.dona-centro span { font-size: 11px; color: var(--ink-soft); }
.dona-ley { display: grid; grid-template-columns: minmax(0, 1fr) max-content 4.6em max-content; column-gap: 10px; row-gap: 2px; align-items: center; font-size: 12.5px; }
.dona-ley.sin-ver { grid-template-columns: minmax(0, 1fr) max-content 4.6em; }
.dona-ley .t { display: flex; align-items: center; gap: 8px; min-width: 0; padding: 5px 6px; margin: 0 -6px; border: none; background: transparent; box-shadow: none; border-radius: 8px; font: inherit; color: var(--ink); text-align: left; cursor: pointer; }
.dona-ley .t:hover { background: var(--bg-alt); transform: none; }
.dona-ley .t:focus-visible { outline: 2px solid var(--gold); outline-offset: 1px; }
.dona-ley .t i { flex: 0 0 auto; width: 12px; height: 12px; border-radius: 3px; border: 2px solid transparent; }
.dona-ley .t[aria-pressed="false"] i { background: transparent !important; }
.dona-ley .t span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.dona-ley .n, .dona-ley .p { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; color: var(--ink); }
.dona-ley .p { font-weight: 600; }
.dona-ley .apagada { opacity: .45; }
.dona-ley .ver { padding: 3px 9px; font-size: 11.5px; border-radius: 999px; box-shadow: none; white-space: nowrap; }
.dona-vacia { display: flex; align-items: center; justify-content: center; text-align: center; font-size: 13px; color: var(--ink-soft); padding: 12px; }
`;

const RAD = Math.PI / 180;

export function Dona({ datos, alto = 220, unidad = 'en total', onAbrir, verTexto = 'Ver', ocultos: ocultosExt, setOcultos: setOcultosExt, fijos, datosTest, formato = entero }: {
  datos: Rebanada[]; alto?: number; unidad?: string; onAbrir?: (clave: string) => void; verTexto?: string; formato?: (n: number) => string;
  ocultos?: Set<string>; setOcultos?: (f: (s: Set<string>) => Set<string>) => void; fijos?: Record<string, string>; datosTest?: string;
}) {
  const [ocultosInt, setOcultosInt] = useState<Set<string>>(new Set());
  const ocultos = ocultosExt || ocultosInt;
  const setOcultos = setOcultosExt || setOcultosInt;
  const colores = useMemo(() => coloresDe(datos, fijos), [datos, fijos]);
  const vis = datos.filter((d) => !ocultos.has(d.clave) && d.n > 0);
  const total = vis.reduce((a, d) => a + d.n, 0);
  const alternar = (k: string) => setOcultos((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const grande = alto > 300;
  const rIn = grande ? 82 : Math.round(alto * 0.25), rOut = grande ? 150 : Math.round(alto * 0.44);

  // El % dentro de la rebanada, sólo si cabe; en las claras el texto va oscuro.
  const etiqueta = (p: any) => {
    const frac = p.value / (total || 1);
    // Una sola rebanada: el 100 % ya lo dicen el centro y la leyenda (y pegado al borde se cortaba).
    if (frac < 0.06 || vis.length < 2) return null;
    const r = (p.innerRadius + p.outerRadius) / 2;
    const x = p.cx + r * Math.cos(-p.midAngle * RAD), y = p.cy + r * Math.sin(-p.midAngle * RAD);
    const fondo = colores.get(p.payload.clave) || NEUTRO;
    const claro = fondo === NEUTRO || /^#(c|d|e|f)/i.test(fondo);
    return <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={grande ? 13 : 11} fontWeight={700} fill={claro ? '#3d3a35' : '#fff'} data-dona-pct style={{ pointerEvents: 'none' }}>{porcentaje(p.value, total)}</text>;
  };

  return (
    <div className="dona" data-dona={datosTest || ''}>
      <style>{CSS}</style>
      {total > 0 ? (
        <div className="dona-graf" style={{ height: alto }}>
          <ResponsiveContainer width="100%" height={alto}>
            <PieChart>
              <Pie data={vis} dataKey="n" nameKey="nombre" cx="50%" cy="50%" innerRadius={rIn} outerRadius={rOut} paddingAngle={vis.length > 1 ? 1.5 : 0}
                stroke="#fff" strokeWidth={vis.length > 1 ? 2 : 0} isAnimationActive={false} label={etiqueta} labelLine={false}
                onClick={onAbrir ? (e: any) => onAbrir(e?.clave || e?.payload?.clave) : undefined} style={{ cursor: onAbrir ? 'pointer' : 'default', outline: 'none' }}>
                {vis.map((d) => <Cell key={d.clave} fill={colores.get(d.clave)} data-rebanada={d.clave} />)}
              </Pie>
              <Tooltip formatter={(v: any, n: any) => [`${formato(Number(v))} · ${porcentaje(Number(v), total)}`, n]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="dona-centro" aria-hidden="true"><b>{formato(total)}</b><span>{unidad}</span></div>
        </div>
      ) : (
        <div className="dona-vacia" style={{ height: alto }}>
          {datos.length ? 'Todo está escondido: toca una fila de abajo para volver a verla.' : 'Sin datos.'}
        </div>
      )}
      <div className={`dona-ley${onAbrir ? '' : ' sin-ver'}`} role="group" aria-label="Mostrar u ocultar en la gráfica" data-dona-ley>
        {datos.map((d) => {
          const on = !ocultos.has(d.clave);
          const c = colores.get(d.clave)!;
          return (
            <div key={d.clave} style={{ display: 'contents' }} data-fila={d.clave}>
              <button type="button" className={`t${on ? '' : ' apagada'}`} aria-pressed={on} onClick={() => alternar(d.clave)}
                title={`${d.titulo || d.nombre} — ${on ? 'tocar para esconder' : 'tocar para volver a enseñar'}`} data-ley={d.clave}>
                <i style={{ background: c, borderColor: c }} /><span>{d.nombre}</span>
              </button>
              <span className={`n${on ? '' : ' apagada'}`} data-ley-n>{formato(d.n)}</span>
              <span className={`p${on ? '' : ' apagada'}`} data-ley-pct>{on ? porcentaje(d.n, total) : '—'}</span>
              {onAbrir && <button type="button" className="btn-ghost ver" onClick={() => onAbrir(d.clave)} aria-label={`${verTexto}: ${d.nombre}`} data-ver={d.clave}>{verTexto}</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
